import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { CheckCircle2, Zap, ArrowRight, Lock, Users } from 'lucide-react';
import { isBetaMode, BETA_CONFIG as LAUNCH_BETA_CONFIG } from '@/lib/launch-mode';
import { BETA_CONFIG } from '@/config/beta';
import { PRICING } from '@/config/pricing';
import { WaitlistModal } from '@/components/landing/WaitlistModal';

const PricingPreview = () => {
  const navigate = useNavigate();
  const betaMode = isBetaMode();
  const [showWaitlistModal, setShowWaitlistModal] = useState(false);

  // During beta, show simplified CTA instead of pricing cards
  if (BETA_CONFIG.isBetaMode && !BETA_CONFIG.showPricing) {
    return (
      <section className="py-20 md:py-28 bg-gradient-to-b from-background to-[hsl(220,25%,6%)]">
        <div className="container">
          <div className="text-center max-w-2xl mx-auto">
            <Badge variant="secondary" className="mb-4">
              Private Beta
            </Badge>
            <h2 className="text-3xl md:text-4xl font-display font-bold mb-4">
              Free During <span className="gradient-text">Beta</span>
            </h2>
            <p className="text-muted-foreground text-lg mb-8">
              Join professionals building intentional careers.
              Full access, no credit card required.
            </p>
            <Button
              size="lg"
              onClick={() => setShowWaitlistModal(true)}
              className="bg-gradient-to-r from-primary to-accent hover:opacity-90 text-primary-foreground"
            >
              Get Beta Access
            </Button>
          </div>
        </div>

        <WaitlistModal
          open={showWaitlistModal}
          onOpenChange={setShowWaitlistModal}
        />
      </section>
    );
  }

  return (
    <section className="py-20 md:py-28 bg-gradient-to-b from-background to-[hsl(220,25%,6%)]">
      <div className="container">
        {/* Section Header */}
        <div className="text-center max-w-2xl mx-auto mb-12">
          <h2 className="text-3xl md:text-4xl font-display font-bold mb-4">
            Simple Pricing That <span className="gradient-text">Scales With You</span>
          </h2>
          <p className="text-muted-foreground text-lg">
            Start free, upgrade when you're ready
          </p>
        </div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto mb-10">
          {/* Free Tier */}
          <div className="rounded-2xl p-6 border border-border/50 bg-card/50 backdrop-blur-sm flex flex-col">
            <h3 className="text-xl font-display font-bold text-foreground">Free</h3>
            <div className="mt-2 mb-4">
              <span className="text-4xl font-display font-bold">$0</span>
              <span className="text-muted-foreground ml-1">/month</span>
            </div>
            
            <ul className="space-y-2.5 mb-6 flex-1">
              {[
                '3 goals',
                '5 habits',
                '1 AI plan',
                'Basic calendar',
              ].map((feature) => (
                <li key={feature} className="flex items-center gap-2.5">
                  <CheckCircle2 className="w-4 h-4 shrink-0 text-accent" />
                  <span className="text-foreground/80 text-sm">{feature}</span>
                </li>
              ))}
            </ul>

            <Button 
              onClick={() => navigate('/auth')}
              variant="outline"
              className="w-full border-border hover:bg-secondary"
            >
              Get Started Free
            </Button>
          </div>

          {/* Pro Tier */}
          <div className="rounded-2xl p-6 border border-primary/50 bg-gradient-to-b from-primary/10 to-card/50 backdrop-blur-sm shadow-lg shadow-primary/10 flex flex-col relative">
            {/* Badge */}
            <div className="absolute -top-3 left-6 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-gradient-to-r from-primary to-accent text-primary-foreground text-xs font-semibold">
              <Zap className="w-3 h-3" />
              Most Popular
            </div>

            <h3 className="text-xl font-display font-bold text-foreground mt-2">Pro</h3>
            <div className="mt-2 mb-4">
              <span className="text-4xl font-display font-bold">${PRICING.pro.monthly}</span>
              <span className="text-muted-foreground ml-1">/month</span>
            </div>
            
            <ul className="space-y-2.5 mb-6 flex-1">
              {[
                'Unlimited goals',
                'Unlimited habits',
                'Weekly AI Coach',
                'Full mentor access',
              ].map((feature) => (
                <li key={feature} className="flex items-center gap-2.5">
                  <CheckCircle2 className="w-4 h-4 shrink-0 text-primary" />
                  <span className="text-foreground/80 text-sm">{feature}</span>
                </li>
              ))}
            </ul>

            <Button 
              onClick={() => navigate('/auth?plan=pro')}
              className="w-full bg-gradient-to-r from-primary to-accent hover:opacity-90 text-primary-foreground"
            >
              Upgrade to Pro
            </Button>
          </div>
        </div>

        {/* Full Comparison Link */}
        <div className="text-center">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/pricing')}
            className="text-muted-foreground hover:text-foreground"
          >
            See Full Comparison
            <ArrowRight className="ml-2 w-4 h-4" />
          </Button>
        </div>
      </div>
    </section>
  );
};

export default PricingPreview;